/**
 * O editor do modelo de treino.
 *
 * **A tela guarda o modelo inteiro em memória e grava de uma vez.** Não é
 * preguiça: é o contrato do servidor, e o motivo está lá — com envio parcial,
 * "removi o último item" fica indistinguível de "não mexi nos itens".
 *
 * A consequência é a trava de versão. Enquanto a pessoa edita, outra tela pode
 * ter salvo; a gravação atrasada leva `409` e **não** apaga o trabalho de
 * ninguém. A tela mostra o conflito e deixa a escolha explícita, em vez de
 * recarregar sozinha por cima do que acabou de ser digitado.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import { PrescriptionForm } from '@/components/PrescriptionForm';
import { LEVELS, MODALITIES } from '@/features/curation/vocabulary';
import type { PrescriptionType, Template } from '@/features/templates/api';
import { type DraftPrescription, missingFields, toPrescription } from '@/features/templates/draft';
import {
  PRESCRIPTION_TYPES,
  emptyPrescription,
  summarize,
  typeForModality,
} from '@/features/templates/prescriptions';
import {
  conflictVersion,
  templateErrorKey,
  useSaveTemplate,
  useTemplate,
} from '@/features/templates/useTemplates';
import { Escolha } from './TemplatesPage';

const GOALS = [
  'general_health',
  'weight_loss',
  'muscle_gain',
  'endurance',
  'mobility',
  'performance',
] as const;

/** O item como a tela o edita: sem identificador, porque a gravação troca tudo. */
interface Rascunho {
  block_label: string | null;
  exercise_id: string | null;
  custom_title: string | null;
  instructions: string | null;
  prescription: DraftPrescription;
}

export default function TemplateEditorPage() {
  const { t } = useTranslation(['templates', 'common']);
  const { templateId = '' } = useParams();
  const modelo = useTemplate(templateId);

  if (modelo.isPending) {
    return (
      <p className="px-5 py-6 text-(--color-sand-700)" aria-busy="true">
        {t('common:state.loading')}
      </p>
    );
  }

  if (modelo.isError || !modelo.data) {
    return (
      <div className="px-5 py-6">
        <Alert tone="error" live="assertive">
          {t(templateErrorKey(modelo.error))}
        </Alert>
        <Link to="/templates" className="mh-button mh-button--ghost mt-4">
          {t('common:action.back')}
        </Link>
      </div>
    );
  }

  // `key` força um editor novo quando o servidor devolve outra versão — é o que
  // faz o estado local recomeçar do que veio, sem um `useEffect` que sincroniza
  // dois lugares e erra em um deles.
  return <Editor key={modelo.data.version} modelo={modelo.data} />;
}

function Editor({ modelo }: { modelo: Template }) {
  const { t } = useTranslation(['templates', 'curation', 'common']);
  const gravar = useSaveTemplate();

  const [title, setTitle] = useState(modelo.title);
  const [modality, setModality] = useState(modelo.modality);
  const [level, setLevel] = useState(modelo.level);
  const [objective, setObjective] = useState(modelo.objective ?? '');
  const [minutes, setMinutes] = useState<string>(
    modelo.estimated_minutes ? String(modelo.estimated_minutes) : '',
  );
  const [itens, setItens] = useState<Rascunho[]>(
    modelo.items.map((item) => ({
      block_label: item.block_label,
      exercise_id: item.exercise_id,
      custom_title: item.custom_title,
      instructions: item.instructions,
      prescription: item.prescription,
    })),
  );
  const [aberto, setAberto] = useState<number | null>(null);

  const conflito = conflictVersion(gravar.error);

  function mexer(indice: number, mudanca: Partial<Rascunho>) {
    setItens(itens.map((item, i) => (i === indice ? { ...item, ...mudanca } : item)));
  }

  function mover(indice: number, direcao: -1 | 1) {
    const destino = indice + direcao;
    if (destino < 0 || destino >= itens.length) return;
    const copia = [...itens];
    const [movido] = copia.splice(indice, 1);
    if (movido) copia.splice(destino, 0, movido);
    setItens(copia);
    setAberto(aberto === indice ? destino : aberto);
  }

  function acrescentar() {
    setItens([
      ...itens,
      {
        block_label: null,
        exercise_id: null,
        custom_title: '',
        instructions: null,
        prescription: emptyPrescription(typeForModality(modality)),
      },
    ]);
    setAberto(itens.length);
  }

  // Duas coisas seguram a gravação, e a tela diz as duas: exercício sem nome
  // e campo obrigatório vazio. Vazio é estado válido enquanto se digita — o
  // que não pode é chegar ao servidor.
  const semNome = itens.some((item) => !(item.custom_title ?? '').trim());
  const pendencias = itens.flatMap((item, indice) =>
    missingFields(item.prescription).map((campo) => ({ indice, campo })),
  );
  const incompleto = semNome || pendencias.length > 0;

  return (
    <div className="px-5 py-6">
      <Link to="/templates" className="mh-button mh-button--ghost -ml-5">
        {t('common:action.back')}
      </Link>

      <h1 className="mt-3 text-2xl">{title || t('templates:untitled')}</h1>

      <label className="mt-4 block">
        <span className="text-sm font-medium text-(--color-sand-800)">
          {t('templates:field.title')}
        </span>
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={160}
          className="mt-1 w-full rounded-xl border border-(--color-sand-200) px-3 py-3"
        />
      </label>

      <Escolha
        label={t('templates:field.modality')}
        value={modality}
        onChange={setModality}
        options={MODALITIES}
        prefix="curation:modality"
      />
      <Escolha
        label={t('templates:field.level')}
        value={level}
        onChange={setLevel}
        options={LEVELS}
        prefix="curation:level"
      />
      <Escolha
        label={t('templates:field.objective')}
        value={objective}
        onChange={setObjective}
        options={GOALS}
        prefix="templates:goal"
        vazio={t('templates:field.noObjective')}
      />

      <label className="mt-3 block">
        <span className="text-sm font-medium text-(--color-sand-800)">
          {t('templates:field.minutes')}{' '}
          <span className="font-normal text-(--color-sand-600)">
            {t('templates:field.optional')}
          </span>
        </span>
        <input
          type="number"
          inputMode="numeric"
          min={5}
          max={300}
          value={minutes}
          onChange={(event) => setMinutes(event.target.value)}
          className="mt-1 w-full rounded-xl border border-(--color-sand-200) px-3 py-3"
        />
      </label>

      <h2 className="mt-8 text-lg font-semibold">
        {t('templates:items.title', { count: itens.length })}
      </h2>

      {itens.length === 0 ? (
        <p className="mt-2 text-sm text-(--color-sand-600)">{t('templates:items.empty')}</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-3">
          {itens.map((item, indice) => (
            <li
              key={indice}
              className="rounded-2xl border border-(--color-sand-200) bg-(--color-sand-0) p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setAberto(aberto === indice ? null : indice)}
                  aria-expanded={aberto === indice}
                  className="flex-1 text-left"
                >
                  <span className="block font-semibold text-(--color-sand-900)">
                    {(item.custom_title ?? '').trim() || t('templates:items.untitled')}
                  </span>
                  {/* O resumo existe para a lista dizer o que o item pede sem
                      abrir o formulário — rolar doze itens abertos num celular
                      não é navegar, é procurar. */}
                  <span className="mt-1 block text-xs text-(--color-sand-600)">
                    {t(`templates:prescription.${item.prescription.type}`)} ·{' '}
                    {summarize(item.prescription, t)}
                  </span>
                </button>

                <span className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => mover(indice, -1)}
                    disabled={indice === 0}
                    aria-label={t('templates:action.moveUp')}
                    className="rounded-lg border-2 border-(--color-sand-300) p-2 disabled:opacity-40"
                  >
                    <Seta direcao="up" />
                  </button>
                  <button
                    type="button"
                    onClick={() => mover(indice, 1)}
                    disabled={indice === itens.length - 1}
                    aria-label={t('templates:action.moveDown')}
                    className="rounded-lg border-2 border-(--color-sand-300) p-2 disabled:opacity-40"
                  >
                    <Seta direcao="down" />
                  </button>
                </span>
              </div>

              {aberto === indice ? (
                <div className="mt-4 border-t border-(--color-sand-200) pt-4">
                  <label className="block">
                    <span className="text-sm font-medium text-(--color-sand-800)">
                      {t('templates:items.name')}
                    </span>
                    <input
                      value={item.custom_title ?? ''}
                      onChange={(event) => mexer(indice, { custom_title: event.target.value })}
                      maxLength={160}
                      className="mt-1 w-full rounded-xl border border-(--color-sand-200) px-3 py-3"
                    />
                  </label>

                  <label className="mt-3 block">
                    <span className="text-sm font-medium text-(--color-sand-800)">
                      {t('templates:items.block')}{' '}
                      <span className="font-normal text-(--color-sand-600)">
                        {t('templates:field.optional')}
                      </span>
                    </span>
                    <input
                      value={item.block_label ?? ''}
                      onChange={(event) =>
                        mexer(indice, { block_label: event.target.value || null })
                      }
                      maxLength={60}
                      className="mt-1 w-full rounded-xl border border-(--color-sand-200) px-3 py-3"
                    />
                  </label>

                  <div className="mt-3">
                    <Escolha
                      label={t('templates:items.prescriptionType')}
                      value={item.prescription.type}
                      onChange={(tipo) =>
                        // Trocar o tipo troca a prescrição inteira: os cinco
                        // formatos não têm campo em comum, e aproveitar valores
                        // entre eles produziria uma mistura que o servidor
                        // recusa.
                        mexer(indice, {
                          prescription: emptyPrescription(tipo as PrescriptionType),
                        })
                      }
                      options={PRESCRIPTION_TYPES}
                      prefix="templates:prescription"
                    />
                  </div>

                  <PrescriptionForm
                    value={item.prescription}
                    onChange={(prescription) => mexer(indice, { prescription })}
                  />

                  <label className="mt-3 block">
                    <span className="text-sm font-medium text-(--color-sand-800)">
                      {t('templates:items.instructions')}{' '}
                      <span className="font-normal text-(--color-sand-600)">
                        {t('templates:field.optional')}
                      </span>
                    </span>
                    <textarea
                      value={item.instructions ?? ''}
                      onChange={(event) =>
                        mexer(indice, { instructions: event.target.value || null })
                      }
                      maxLength={2000}
                      rows={3}
                      className="mt-1 w-full rounded-xl border border-(--color-sand-200) px-3 py-3"
                    />
                  </label>

                  <button
                    type="button"
                    onClick={() => {
                      setItens(itens.filter((_, i) => i !== indice));
                      setAberto(null);
                    }}
                    className="mh-button mh-button--ghost mt-3 text-sm"
                  >
                    {t('templates:action.removeItem')}
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <button type="button" onClick={acrescentar} className="mh-button mh-button--ghost mt-3">
        {t('templates:action.addItem')}
      </button>

      {conflito !== null ? (
        <div className="mt-5">
          <Alert tone="warning" live="assertive">
            <p className="font-semibold">{t('templates:conflict.title')}</p>
            <p className="mt-1">{t('templates:conflict.body')}</p>
          </Alert>
        </div>
      ) : gravar.isError ? (
        <div className="mt-5">
          <Alert tone="error" live="assertive">
            {t(templateErrorKey(gravar.error))}
          </Alert>
        </div>
      ) : null}

      {semNome ? (
        <p className="mt-3 text-sm text-(--color-sand-700)">{t('templates:items.needName')}</p>
      ) : null}

      {pendencias.length > 0 ? (
        <p className="mt-3 text-sm text-(--color-sand-700)">
          {t('templates:items.needFields', {
            fields: [
              ...new Set(pendencias.map((p) => t(`templates:prescriptionField.${p.campo}`))),
            ].join(', '),
          })}
        </p>
      ) : null}

      <button
        type="button"
        disabled={title.trim() === '' || incompleto || gravar.isPending}
        onClick={() =>
          gravar.mutate({
            templateId: modelo.template_id,
            version: modelo.version,
            draft: {
              title: title.trim(),
              modality,
              level,
              objective: objective || null,
              estimated_minutes: minutes === '' ? null : Number(minutes),
              // `toPrescription` devolve `null` se faltar obrigatório, e o
              // botão está desabilitado justamente nesse caso — o `flatMap`
              // com lista vazia é o que torna isso verdade para o compilador
              // também, sem um `as` que só afirmaria.
              items: itens.flatMap((item) => {
                const prescription = toPrescription(item.prescription);
                if (prescription === null) return [];
                return [
                  {
                    block_label: item.block_label,
                    exercise_id: item.exercise_id,
                    custom_title: (item.custom_title ?? '').trim(),
                    instructions: item.instructions,
                    prescription,
                  },
                ];
              }),
            },
          })
        }
        className="mh-button mh-button--primary mt-5 w-full"
      >
        {gravar.isPending ? t('common:state.loading') : t('common:action.save')}
      </button>

      <Salvo gravando={gravar.isPending} sucesso={gravar.isSuccess} />
    </div>
  );
}

/**
 * O aviso de que gravou.
 *
 * Some sozinho: é confirmação, não decisão. Um alerta permanente no fim de um
 * formulário longo vira ruído que se aprende a ignorar — e aí o próximo, que
 * importa, também passa despercebido.
 */
function Salvo({ gravando, sucesso }: { gravando: boolean; sucesso: boolean }) {
  const { t } = useTranslation(['templates']);
  const [visivel, setVisivel] = useState(false);

  useEffect(() => {
    if (!sucesso || gravando) return;
    setVisivel(true);
    const relogio = setTimeout(() => setVisivel(false), 4000);
    return () => clearTimeout(relogio);
  }, [sucesso, gravando]);

  if (!visivel) return null;

  return (
    <div className="mt-3">
      <Alert tone="success">{t('templates:saved')}</Alert>
    </div>
  );
}

/**
 * A seta de reordenar, desenhada em vez de digitada.
 *
 * As setas de texto (`↑` `↓`) saem finas e do tamanho da fonte, e cada sistema
 * desenha a sua. Um traço de 2,5 as torna visíveis num cartão cheio de texto —
 * e o alvo de toque cresce junto, que num celular importa mais do que o
 * desenho.
 */
function Seta({ direcao }: { direcao: 'up' | 'down' }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {direcao === 'up' ? (
        <>
          <path d="M12 19V5" />
          <path d="m5 12 7-7 7 7" />
        </>
      ) : (
        <>
          <path d="M12 5v14" />
          <path d="m19 12-7 7-7-7" />
        </>
      )}
    </svg>
  );
}
